import { Spacing } from 'zens-storybook'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { AccessType, Draft, useStore, useWorkspaces, useZoneWorkspacesMutation } from 'zens-store'
import { useNumberParams } from 'zens-router'
import { ZoneInfo, LayoutHeader, ZoneHeader, ZoneFloorplan, ZoneWorkspace } from '@dms/component'
import { usePermissions } from '@dms/hook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    margin: Spacing.L,
  },
  workspaces: {
    marginTop: Spacing.XL,
  },
})

const ScreenZoneFloorplan = () => {
  const { message } = useStore()
  const { t } = useTranslation()
  const [{ companyId, floorId, zoneId }, setNumberParam] = useNumberParams()
  const mutation = useZoneWorkspacesMutation({ companyId, zoneId })
  const { hasPermission } = usePermissions(companyId)
  const workspaces = useWorkspaces({ companyId, filter: { zoneId: [zoneId] } })

  function updateFloor(floorId: number) {
    setNumberParam({ floorId })
  }

  async function saveWorkspaces(data: ZoneWorkspace[]) {
    try {
      const added = data.filter((item) => item.zoneId === zoneId).map((item) => item.workspace.id)
      await mutation.add(new Draft<any>({ id: zoneId, workspaces: added }))
      const removed = data.filter((item) => item.zoneId !== zoneId).map((item) => item.workspace)
      await Promise.all(removed.map(async (workspace) => await mutation.delete(new Draft(workspace.data))))
      workspaces.refetch()
      message.add({
        message: t('Workspaces succesfully updated'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`ZONE: Update workspace failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspaces could not be updated')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <ZoneHeader companyId={companyId} zoneId={zoneId} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ZoneInfo.Skeleton companyId={companyId} />}>
          <ZoneInfo
            showFloors
            companyId={companyId}
            zoneId={zoneId}
            buttonHref={
              hasPermission(AccessType.Update, 'workspace')
                ? `/companies/${companyId}/workspaces/zones/${zoneId}/edit`
                : undefined
            }
          />
        </Suspense>
        <Suspense fallback={<ZoneFloorplan.Skeleton style={styles.workspaces} companyId={companyId} />}>
          <ZoneFloorplan
            style={styles.workspaces}
            companyId={companyId}
            zoneId={zoneId}
            floorId={floorId}
            onChangeFloor={updateFloor}
            onSave={saveWorkspaces}
          />
        </Suspense>
      </View>
    </View>
  )
}

export default ScreenZoneFloorplan
